<script setup lang="ts">
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { getMinValueToTriggerModule } = useModulesValidation();
const { showMoneyFormat } = useWallet();
const { isFullOnboarding } = useKyc();

const currentPendencyName = computed(() => {
  return $containment.currentPendency;
});

const currentContextName = computed(() => {
  return $containment.currentContext;
});

const currentContextModules = computed(() => {
  return $containment.currentContextModules;
});

const currentContextValidationState = computed(() => {
  return $containment.currentContextValidationState;
});

const combinedModulesState = computed(() => {
  if (!currentContextModules.value || !currentContextValidationState.value) {
    return [];
  }

  const modules = currentContextModules.value;
  const validationStates = currentContextValidationState.value;
  const currentPendency = currentPendencyName.value;

  return modules
    .map((module) => {
      const validationState = validationStates.find(
        (state) => state.module === module.name,
      );

      return {
        ...module,
        isValid: validationState?.isValid ?? false,
        isCurrent: module.name === currentPendency,
      };
    })
    .filter((module) => {
      if (currentPendency !== 'docs') {
        return module.name !== 'docs';
      }

      return true;
    });
});

function isFrequentOrLiveness(moduleItem: any) {
  if (moduleItem.name === 'kyc' && moduleItem.frequent) {
    return true;
  }

  if (moduleItem.name === 'kyc' && !isFullOnboarding.value) {
    return true;
  }

  return moduleItem.frequent;
}

function getModuleName(moduleItem: any) {
  if (isFrequentOrLiveness(moduleItem) && moduleItem.name === 'kyc') {
    return t('verification.message.module.liveness');
  }

  return t(`verification.message.module.${moduleItem.name}`);
}

function getModuleIcon(moduleItem: any) {
  if (isFrequentOrLiveness(moduleItem)) {
    return 'clock';
  }

  return moduleItem.isValid ? 'check' : 'close';
}

function getModuleClass(moduleItem: any) {
  if (isFrequentOrLiveness(moduleItem)) {
    return 'warning';
  }

  return moduleItem.isValid ? 'success' : 'error';
}

function getMinValueFormatted(name: keyof ValidationModules) {
  if (!name) {
    return '';
  }

  const minValue = getMinValueToTriggerModule(name);

  if (!minValue) {
    return '';
  }

  const value = minValue / 100;

  return showMoneyFormat({ value });
}

function getContextMessage(name: any) {
  if (!name) {
    return '';
  }

  const minValue = getMinValueToTriggerModule(name);

  if (!minValue || minValue <= 99) {
    return t(`verification.message.context.${name}`);
  }

  return t(`verification.message.context.with_min_value.${name}`, {
    minValue: getMinValueFormatted(name),
  });
}
</script>

<template>
  <div
    v-if="currentContextName"
    :class="$style.stepMessage"
  >
    <span :class="$style.stepMessageText">
      {{ getContextMessage(currentContextName) }}
    </span>

    <div :class="$style.stepMessageModules">
      <span
        v-for="moduleItem in combinedModulesState"
        :key="moduleItem.name"
        :class="[
          $style.stepMessageModule,
          $style[getModuleClass(moduleItem)],
          {
            [$style.active]: moduleItem.isCurrent,
          },
        ]"
      >
        <NuxtIcon
          :class="[
            $style.stepMessageModuleIcon,
            $style[getModuleClass(moduleItem)],
          ]"
          :name="getModuleIcon(moduleItem)"
        />

        <span :class="$style.stepMessageModuleText">
          {{ getModuleName(moduleItem) }}
        </span>
      </span>
    </div>
  </div>
</template>

<style module lang="postcss">
.stepMessage {
  @apply flex flex-col gap-3 pt-4 pb-6 px-2 mx-6 border-b border-dashed border-white/10;

  .stepMessageText {
    @apply text-xs md:text-sm font-semibold;
  }

  .stepMessageModules {
    @apply flex items-center justify-center flex-wrap gap-2;

    .stepMessageModule {
      @apply flex items-center justify-center bg-auth-bg-inputs rounded-full gap-1.5 pr-2 border border-solid border-white/5 opacity-60;

      &.warning {
        @apply bg-warning/30;

        &.active {
          @apply border-warning/50 opacity-100;
        }
      }

      &.success {
        @apply bg-success/30;

        &.active {
          @apply border-success/50 opacity-100;
        }
      }

      &.error {
        @apply bg-error/30;

        &.active {
          @apply border-error/50 opacity-100;
        }
      }

      .stepMessageModuleIcon {
        @apply text-xs w-5 h-5 rounded-full bg-white/20 text-white flex items-center justify-center;

        &.warning {
          @apply bg-warning;
        }

        &.success {
          @apply bg-success;
        }

        &.error {
          @apply bg-error;
        }
      }

      .stepMessageModuleText {
        @apply text-xs font-semibold whitespace-nowrap;
      }
    }
  }
}
</style>
